import {
  evaluateRules,
  type RuleFailure,
  SETUP_RULES,
  type SetupRule,
} from "@/shared/lib/raidRules";
import { RAID_ROLES } from "@/shared/lib/roles";
import type {
  BossSetupBoss,
  BossSetupContent,
  PlayerKind,
  SetupPlayer,
  SetupResponse,
} from "./setup.types";

export type BossParticipant = {
  id: string;
  kind: PlayerKind;
};

export type CreatedStandIn = Pick<
  SetupPlayer,
  "id" | "name" | "wowClass" | "raidRole" | "benched"
>;

export type VisibleBoss = BossSetupBoss & {
  raidId: string;
  raidName: string;
  isGroupStart: boolean;
  isGroupEnd: boolean;
  // Skipped this raid day: excluded from vault counts and rendered inert. Assignments
  // are preserved in the cache untouched, so un-skipping restores them.
  skipped: boolean;
};

export type BossGroupBoundary = {
  raidId: string;
  raidName: string;
  startIndex: number;
  endIndexExclusive: number;
};

export type FlattenedBossContent = {
  bosses: VisibleBoss[];
  groups: BossGroupBoundary[];
};

export type VaultTier = 0 | 1 | 2 | 3;
export type VaultThresholds = readonly [
  number | string,
  number | string,
  number | string,
];

const roleOrder: ReadonlyMap<string, number> = new Map(
  RAID_ROLES.map((role, index) => [role, index]),
);
const swedishNameComparer = new Intl.Collator("sv-SE", {
  sensitivity: "accent",
});

function compareNullableClass(
  left: string | null,
  right: string | null,
): number {
  if (left === null) return right === null ? 0 : 1;
  if (right === null) return -1;
  return left < right ? -1 : left > right ? 1 : 0;
}

function participantKind(player: SetupPlayer): PlayerKind | null {
  return player.kind === "member" || player.kind === "stand_in"
    ? player.kind
    : null;
}

export function isActiveSetupPlayer(player: SetupPlayer): boolean {
  return !player.benched && participantKind(player) !== null;
}

// Same order as the backend roster: raid-role ordinal (null last), then wow class
// (null last), then case-insensitive Swedish name.
export function flattenActivePlayers(players: SetupPlayer[]): SetupPlayer[] {
  return players.filter(isActiveSetupPlayer).sort((left, right) => {
    const leftRole = roleOrder.get(left.raidRole ?? "") ?? RAID_ROLES.length;
    const rightRole = roleOrder.get(right.raidRole ?? "") ?? RAID_ROLES.length;
    return (
      leftRole - rightRole ||
      compareNullableClass(left.wowClass, right.wowClass) ||
      swedishNameComparer.compare(left.name, right.name)
    );
  });
}

function bySortPosition(
  left: { sortPosition: number | string },
  right: { sortPosition: number | string },
): number {
  return Number(left.sortPosition) - Number(right.sortPosition);
}

// Sort copies instead of trusting transport order, and retain both per-column
// divider flags and explicit half-open group boundaries for the matrix header.
export function flattenBossContent(
  content: BossSetupContent,
): FlattenedBossContent {
  const bosses: VisibleBoss[] = [];
  const groups: BossGroupBoundary[] = [];
  const skipped = new Set(content.skippedBossIds);

  for (const raid of [...content.raids].sort(bySortPosition)) {
    const raidBosses = [...raid.bosses].sort(bySortPosition);
    const startIndex = bosses.length;
    raidBosses.forEach((boss, index) => {
      bosses.push({
        ...boss,
        raidId: raid.id,
        raidName: raid.name,
        isGroupStart: index === 0,
        isGroupEnd: index === raidBosses.length - 1,
        skipped: skipped.has(boss.id),
      });
    });
    groups.push({
      raidId: raid.id,
      raidName: raid.name,
      startIndex,
      endIndexExclusive: bosses.length,
    });
  }

  return { bosses, groups };
}

export function isBossAssigned(
  content: BossSetupContent,
  bossId: string,
  participant: BossParticipant,
): boolean {
  return participant.kind === "member"
    ? content.memberAssignments.some(
        (assignment) =>
          assignment.bossId === bossId && assignment.userId === participant.id,
      )
    : content.standInAssignments.some(
        (assignment) =>
          assignment.bossId === bossId &&
          assignment.standInId === participant.id,
      );
}

// O(1) membership lookups for the matrix render, which checks every
// player/boss cell (and re-checks on hover). Keyed by boss + kind + id.
export type AssignmentLookup = ReadonlySet<string>;

function assignmentKey(
  bossId: string,
  kind: PlayerKind,
  participantId: string,
): string {
  return `${bossId}\0${kind}:${participantId}`;
}

export function buildAssignmentLookup(
  content: BossSetupContent,
): AssignmentLookup {
  const lookup = new Set<string>();
  for (const assignment of content.memberAssignments) {
    lookup.add(assignmentKey(assignment.bossId, "member", assignment.userId));
  }
  for (const assignment of content.standInAssignments) {
    lookup.add(
      assignmentKey(assignment.bossId, "stand_in", assignment.standInId),
    );
  }
  return lookup;
}

export function hasAssignment(
  lookup: AssignmentLookup,
  bossId: string,
  participant: BossParticipant,
): boolean {
  return lookup.has(assignmentKey(bossId, participant.kind, participant.id));
}

export type RowState = {
  participantKind: PlayerKind;
  roleChanged: boolean;
  isLast: boolean;
  selections: boolean[];
  selectedBossCount: number;
};

// Per-row derivation for the boss matrix: the participant kind, whether the raid-role divider
// starts here (differs from the previous row), the last-row flag, and a single assignment pass
// the vault count and the per-boss cells both read from (avoids re-calling hasAssignment per boss).
export function deriveRowState(
  player: SetupPlayer,
  index: number,
  players: SetupPlayer[],
  bosses: VisibleBoss[],
  lookup: AssignmentLookup,
): RowState {
  const participantKind: PlayerKind =
    player.kind === "member" ? "member" : "stand_in";
  const previous = players[index - 1];
  const roleChanged = Boolean(
    previous && previous.raidRole !== player.raidRole,
  );
  const isLast = index === players.length - 1;
  const selections = bosses.map((boss) =>
    hasAssignment(lookup, boss.id, { id: player.id, kind: participantKind }),
  );
  // Skipped bosses never count toward the vault, regardless of a preserved assignment.
  const selectedBossCount = selections.filter(
    (selected, bossIndex) => selected && !bosses[bossIndex].skipped,
  ).length;
  return {
    participantKind,
    roleChanged,
    isLast,
    selections,
    selectedBossCount,
  };
}

export function setAssignmentMembership(
  content: BossSetupContent,
  bossId: string,
  participant: BossParticipant,
  selected: boolean,
): BossSetupContent {
  const currentlySelected = isBossAssigned(content, bossId, participant);
  if (currentlySelected === selected) return content;

  if (participant.kind === "member") {
    return {
      ...content,
      memberAssignments: selected
        ? [...content.memberAssignments, { bossId, userId: participant.id }]
        : content.memberAssignments.filter(
            (assignment) =>
              assignment.bossId !== bossId ||
              assignment.userId !== participant.id,
          ),
    };
  }

  return {
    ...content,
    standInAssignments: selected
      ? [...content.standInAssignments, { bossId, standInId: participant.id }]
      : content.standInAssignments.filter(
          (assignment) =>
            assignment.bossId !== bossId ||
            assignment.standInId !== participant.id,
        ),
  };
}

function activeParticipant(
  data: SetupResponse,
  participant: BossParticipant,
): SetupPlayer | undefined {
  return data.players.find(
    (player) =>
      player.id === participant.id &&
      player.kind === participant.kind &&
      isActiveSetupPlayer(player),
  );
}

function withBossContent(
  data: SetupResponse,
  update: (content: BossSetupContent) => BossSetupContent,
): SetupResponse {
  if (!data.bossContent) return data;
  const bossContent = update(data.bossContent);
  return bossContent === data.bossContent ? data : { ...data, bossContent };
}

function hasVisibleBoss(content: BossSetupContent, bossId: string): boolean {
  return content.raids.some((raid) =>
    raid.bosses.some((boss) => boss.id === bossId),
  );
}

export function createBossViewOptimistic(data: SetupResponse): SetupResponse {
  const active = flattenActivePlayers(data.players);
  return withBossContent(data, (content) => {
    const bossIds = flattenBossContent(content).bosses.map((boss) => boss.id);
    return {
      ...content,
      isCreated: true,
      memberAssignments: active
        .filter((player) => player.kind === "member")
        .flatMap((player) =>
          bossIds.map((bossId) => ({ bossId, userId: player.id })),
        ),
      standInAssignments: active
        .filter((player) => player.kind === "stand_in")
        .flatMap((player) =>
          bossIds.map((bossId) => ({ bossId, standInId: player.id })),
        ),
    };
  });
}

export function addCreatedStandInOptimistic(
  data: SetupResponse,
  created: CreatedStandIn,
): SetupResponse {
  if (
    data.players.some(
      (player) => player.kind === "stand_in" && player.id === created.id,
    )
  ) {
    return data;
  }

  const player: SetupPlayer = {
    ...created,
    kind: "stand_in",
    baseWowClass: null,
    baseRaidRole: null,
    overrideWowClass: null,
    overrideRaidRole: null,
    isMe: false,
  };
  const withPlayer = { ...data, players: [...data.players, player] };
  return data.bossContent?.isCreated && !created.benched
    ? setPlayerBossesOptimistic(
        withPlayer,
        { id: created.id, kind: "stand_in" },
        true,
      )
    : withPlayer;
}

export function deleteBossViewOptimistic(data: SetupResponse): SetupResponse {
  return withBossContent(data, (content) => ({
    ...content,
    isCreated: false,
    memberAssignments: [],
    standInAssignments: [],
    skippedBossIds: [],
  }));
}

export function setBossAssignmentOptimistic(
  data: SetupResponse,
  bossId: string,
  participant: BossParticipant,
  selected: boolean,
): SetupResponse {
  if (!activeParticipant(data, participant)) return data;
  return withBossContent(data, (content) => {
    if (!content.isCreated || !hasVisibleBoss(content, bossId)) return content;
    return setAssignmentMembership(content, bossId, participant, selected);
  });
}

export function setPlayerBossesOptimistic(
  data: SetupResponse,
  participant: BossParticipant,
  selected: boolean,
): SetupResponse {
  if (!activeParticipant(data, participant)) return data;
  return withBossContent(data, (content) => {
    if (!content.isCreated) return content;
    return flattenBossContent(content).bosses.reduce(
      (next, boss) =>
        setAssignmentMembership(next, boss.id, participant, selected),
      content,
    );
  });
}

function replaceBossAssignments(
  content: BossSetupContent,
  bossId: string,
  selectedPlayers: SetupPlayer[],
): BossSetupContent {
  return {
    ...content,
    memberAssignments: [
      ...content.memberAssignments.filter(
        (assignment) => assignment.bossId !== bossId,
      ),
      ...selectedPlayers
        .filter((player) => player.kind === "member")
        .map((player) => ({ bossId, userId: player.id })),
    ],
    standInAssignments: [
      ...content.standInAssignments.filter(
        (assignment) => assignment.bossId !== bossId,
      ),
      ...selectedPlayers
        .filter((player) => player.kind === "stand_in")
        .map((player) => ({ bossId, standInId: player.id })),
    ],
  };
}

export function setBossPlayersOptimistic(
  data: SetupResponse,
  bossId: string,
  selected: boolean,
): SetupResponse {
  return withBossContent(data, (content) => {
    if (!content.isCreated || !hasVisibleBoss(content, bossId)) return content;
    return replaceBossAssignments(
      content,
      bossId,
      selected ? flattenActivePlayers(data.players) : [],
    );
  });
}

function assignedActivePlayers(
  content: BossSetupContent,
  bossId: string,
  activePlayers: SetupPlayer[],
): SetupPlayer[] {
  return activePlayers.filter((player) => {
    const kind = participantKind(player);
    return (
      kind !== null && isBossAssigned(content, bossId, { id: player.id, kind })
    );
  });
}

export function selectedPlayersForBoss(
  data: SetupResponse,
  bossId: string,
): SetupPlayer[] {
  if (!data.bossContent?.isCreated) return [];
  return assignedActivePlayers(
    data.bossContent,
    bossId,
    flattenActivePlayers(data.players),
  );
}

export function buildBossPlayerLists(
  data: SetupResponse,
): Map<string, SetupPlayer[]> {
  const result = new Map<string, SetupPlayer[]>();
  if (!data.bossContent) return result;
  const content = data.bossContent;
  const active = flattenActivePlayers(data.players);
  for (const boss of flattenBossContent(content).bosses) {
    result.set(
      boss.id,
      content.isCreated ? assignedActivePlayers(content, boss.id, active) : [],
    );
  }
  return result;
}

export function setBossSkippedOptimistic(
  data: SetupResponse,
  bossId: string,
  skipped: boolean,
): SetupResponse {
  return withBossContent(data, (content) => {
    if (!content.isCreated || !hasVisibleBoss(content, bossId)) return content;
    // Tolerate a cached response predating the skip feature (field absent).
    const current = content.skippedBossIds ?? [];
    const already = current.includes(bossId);
    if (already === skipped) return content;
    return {
      ...content,
      skippedBossIds: skipped
        ? [...current, bossId]
        : current.filter((id) => id !== bossId),
    };
  });
}

export function copyPreviousBossOptimistic(
  data: SetupResponse,
  bossId: string,
): SetupResponse {
  if (!data.bossContent?.isCreated) return data;
  const bosses = flattenBossContent(data.bossContent).bosses;
  const bossIndex = bosses.findIndex((boss) => boss.id === bossId);
  if (bossIndex <= 0) return data;
  const selected = selectedPlayersForBoss(data, bosses[bossIndex - 1].id);
  return withBossContent(data, (content) =>
    replaceBossAssignments(content, bossId, selected),
  );
}

// Removes stale keys for benched, absent, deleted, or malformed participants.
// Absentees are absent from `players`, so the same active-set intersection handles
// every unavailable state without consulting UI-only absence details.
export function removeUnavailableAssignments(
  data: SetupResponse,
): SetupResponse {
  const activeMembers = new Set(
    data.players
      .filter(
        (player) => isActiveSetupPlayer(player) && player.kind === "member",
      )
      .map((player) => player.id),
  );
  const activeStandIns = new Set(
    data.players
      .filter(
        (player) => isActiveSetupPlayer(player) && player.kind === "stand_in",
      )
      .map((player) => player.id),
  );
  return withBossContent(data, (content) => ({
    ...content,
    memberAssignments: content.memberAssignments.filter((assignment) =>
      activeMembers.has(assignment.userId),
    ),
    standInAssignments: content.standInAssignments.filter((assignment) =>
      activeStandIns.has(assignment.standInId),
    ),
  }));
}

export function vaultTier(
  assignedBossCount: number,
  thresholds: VaultThresholds,
): VaultTier {
  const [first, second, third] = thresholds.map(Number);
  if (assignedBossCount >= third) return 3;
  if (assignedBossCount >= second) return 2;
  if (assignedBossCount >= first) return 1;
  return 0;
}

export function ruleFailuresForPlayers(
  players: SetupPlayer[],
  rules: SetupRule[] = SETUP_RULES,
): RuleFailure[] {
  return evaluateRules(
    players,
    rules.filter((rule) => rule.count.kind !== "total"),
  );
}
